/**
 * Configuración de la vista de lista "Etiquetas" (Tags).
 * ListView + config; acciones primaria y empty abren modal (onAddTag).
 */

import { ProductIcon } from '@/lib/polaris-icons';
import type { ListViewConfig } from '@/types/listView.types';
import { ROUTES } from '@/config/routes.config';

export interface TagListItem {
  id: string;
  name: string;
  tenant_id: string;
  created_at: string;
  /** Cantidad de productos que tienen esta etiqueta */
  product_count?: number;
  /** Opcional: si la tabla tiene is_active en el futuro */
  is_active?: boolean;
}

export type NavigateFn = (path: string) => void;

/**
 * Config de lista de etiquetas. toUrl para navegación (back); openAddModal para acción primaria y empty state.
 */
export function getTagsListConfig(
  toUrl: NavigateFn,
  openAddModal: () => void
): ListViewConfig<TagListItem> {
  return {
    title: 'Etiquetas',
    titleIcon: ProductIcon,
    onBack: () => toUrl(ROUTES.PRODUCTS),
    backAccessibilityLabel: 'Volver a Productos',
    primaryAction: {
      onAction: openAddModal,
    },
    resourceName: { singular: 'etiqueta', plural: 'etiquetas' },
    columns: [
      {
        key: 'name',
        title: 'Nombre',
        type: 'badge',
        format: (v) => (v != null ? String(v) : '—'),
      },
      {
        key: 'product_count',
        title: 'Productos',
        type: 'number',
        format: (value, _item) =>
          `${value ?? 0} ${(value ?? 0) === 1 ? 'producto' : 'productos'}`,
      },
      {
        key: 'is_active',
        title: 'Estado',
        type: 'badge',
        map: { true: 'Activo', false: 'Inactivo' },
        format: (v) => (v === false ? 'Inactivo' : 'Activo'),
        // product_tags no tiene is_active en DB: undefined/true → verde, solo false → gris
        toneFromValue: (v) => (v === false ? 'subdued' : 'success'),
      },
      {
        key: 'actions',
        title: 'Acciones',
        type: 'actions',
        editLabel: 'Editar',
        deleteLabel: 'Eliminar',
        delete: true,
      },
    ],
    emptyState: {
      heading: 'No hay etiquetas creadas',
      description: 'Crea etiquetas para organizar y filtrar tus productos (ej: Oferta, Nuevo, Vegetariano).',
      image: 'https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png',
      action: {
        content: 'Agregar etiqueta',
        onAction: openAddModal,
      },
    },
    bulkActions: [
      { content: 'Exportar datos', actionKey: 'export' },
      // "Eliminar" lo inyecta la plantilla vía prop `bulkDelete`.
    ],
    idKey: 'id',
  };
}
